import React, { useEffect, useState } from 'react';
import { Chip, Container, Grid } from '@mui/material';
import { useQuery } from 'react-query';
import { getAssets } from '../../mutations/queries';
import { QueryKeys } from '../../react-query/QueryKeys';
import { toast } from 'react-toastify';
import Loading from '../../components/Loading';
import NoAsset from '../../components/NoAssets';
import AssetCard from '../../components/AssetCard';
import { Asset } from '../../types/assets';
import { TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const Home = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllAssets, setShowAllAssets] = useState(true);
  const [sortByCreatedAt, setSortByCreatedAt] = useState(false);
  const [selectedChip, setSelectedChip] = useState<null | string>(null);

  const {
    data: assets = [],
    isLoading,
    isError,
  } = useQuery(QueryKeys.ASSETS, () => getAssets());

  if (isError) {
    toast.error('Something went wrong');
  }

  useEffect(() => {
    if (searchQuery) {
      setShowAllAssets(false);
    }
  }, [searchQuery]);

  if (isLoading) {
    return <Loading />;
  }

  const sortedAssets = sortByCreatedAt
    ? [...assets].sort(
        (a, b) => +new Date(b.created_at) - +new Date(a.created_at)
      )
    : assets;

  const filteredAssets = sortedAssets?.filter((asset: Asset) => {
    const companyFields = Object.values(asset.company).join(' ').toLowerCase();
    const investmentInfoFields = Object.values(asset.investmentInfo)
      .join(' ')
      .toLowerCase();

    const teamFields = asset.team
      ? asset.team
          .map((teamMember) =>
            Object.values(teamMember).join(' ').toLowerCase()
          )
          .join(' ')
      : '';

    const leadInvestorFields = asset.leadInvestors
      ? asset.leadInvestors
          .map((teamMember) =>
            Object.values(teamMember).join(' ').toLowerCase()
          )
          .join(' ')
      : '';

    const sustainabilityGoalsFields = asset.sustainabilityGoals
      ? Object.values(asset.sustainabilityGoals).join(' ').toLowerCase()
      : '';
    const overViewFields = asset.overview
      ? Object.values(asset.overview).join(' ').toLowerCase()
      : '';

    const searchWords = searchQuery.toLowerCase().split(' ');

    const isCompanyMatch =
      searchWords.every((word) => companyFields.includes(word)) || !searchQuery;

    const isInvestmentInfoMatch =
      searchWords.every((word) => investmentInfoFields.includes(word)) ||
      !searchQuery;

    const isLeadInvestorMatch =
      searchWords.every((word) => leadInvestorFields.includes(word)) ||
      !searchQuery;

    const isTeamMatch =
      searchWords.every((word) => teamFields.includes(word)) || !searchQuery;

    const isSustainabilityGoalsMatch =
      searchWords.every((word) => sustainabilityGoalsFields.includes(word)) ||
      !searchQuery;

    const isOverViewMatch =
      searchWords.every((word) => overViewFields.includes(word)) ||
      !searchQuery;

    const isMatchingChip =
      selectedChip === null ||
      (selectedChip === 'recentlyLaunched' && sortByCreatedAt) ||
      (selectedChip === 'allAssets' && !sortByCreatedAt);

    return (
      isMatchingChip &&
      (showAllAssets ||
        isCompanyMatch ||
        isInvestmentInfoMatch ||
        isLeadInvestorMatch ||
        isTeamMatch ||
        isSustainabilityGoalsMatch ||
        isOverViewMatch)
    );
  });

  const handleAllAssetsClick = () => {
    setShowAllAssets(true);
    setSearchQuery('');
    setSortByCreatedAt(false);
    setSelectedChip('allAssets');
  };

  const handleRecentlyLaunchedClick = () => {
    setSortByCreatedAt(true);
    setSelectedChip('recentlyLaunched');
  };

  return (
    <Container
      maxWidth="xl"
      style={{
        position: 'relative',
      }}
    >
      {!isLoading && assets && assets?.length === 0 ? (
        <NoAsset />
      ) : (
        <>
          <Grid
            container
            justifyContent={'flex-end'}
            style={{ marginTop: '20px' }}
          >
            <Grid xs={12} md={10} item>
              <Chip
                label="All Assets"
                clickable
                color={selectedChip === 'allAssets' ? 'primary' : 'default'}
                onClick={handleAllAssetsClick}
                variant={selectedChip === 'allAssets' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Recently Launched"
                variant={
                  selectedChip === 'recentlyLaunched' ? 'filled' : 'outlined'
                }
                color={
                  selectedChip === 'recentlyLaunched' ? 'primary' : 'default'
                }
                clickable
                sx={{ marginLeft: '2px' }}
                onClick={handleRecentlyLaunchedClick}
              />
            </Grid>
            <Grid xs={12} md={2} item>
              <TextField
                placeholder="Search"
                variant={'standard'}
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      style={{
                        cursor: 'pointer',
                      }}
                    />
                  ),
                }}
                sx={{
                  '& input': {
                    borderRadius: '50px',
                    background: '#AAD0D4',
                    border: 'none',
                    marginX: '10px',

                    '&::placeholder': {
                      color: 'black',
                      opacity: 1,
                    },

                    '&:focus': {
                      border: 'none',
                      outline: 'none',
                    },
                  },
                  '& .MuiInput-root': {
                    background: '#AAD0D4',
                    borderRadius: '50px',
                    padding: '10px',
                    '&:focus': {
                      border: 'none',
                      outline: 'none',
                    },
                  },
                  '& .MuiInputBase-input': {
                    color: 'black',
                    background: '#AAD0D4',
                  },

                  '& .MuiInput-underline:before': {
                    borderBottom: 'none',
                  },

                  '& .MuiInput-underline:after': {
                    borderBottom: 'none',
                  },

                  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                    borderBottom: 'none',
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            {filteredAssets?.map(
              (asset: Asset, id: React.Key | null | undefined) => (
                <Grid key={id} item xs={12} md={6} lg={4}>
                  <AssetCard asset={asset} />
                </Grid>
              )
            )}
          </Grid>
        </>
      )}
    </Container>
  );
};

export default Home;
